/**
 * Turning a whole Lens board into a PDF a person can send on: a partner-ready report, not a
 * screenshot of the admin page.
 *
 * Card-level exports (CSV rows, a chart PNG) live in `lensExport.ts`. This module reads the
 * board as the reader sees it and typesets it as a report, in the house style of the bedbank
 * partnership reports: a cream page, a deep-teal header band, white KPI tiles, ruled tables
 * and a quiet footer.
 *
 * - Metric cards become KPI tiles, typeset from what the card paints: its figure (or the word
 *   or sentence it shows instead: "Partial", a refusal reason, "no number to show"), and its
 *   comparison line with the same qualifiers and period note as on the board.
 * - Table cards become real tables, built from the painted table's own cells. They break
 *   only between rows and repeat their header row on every page, so nothing is cut or lost.
 *   When a card shows only part of its rows, the PDF says so in the board's own words.
 * - Insight cards become complete prose, including saved-note scope notices. Their headings,
 *   paragraphs, lists and table text continue onto later pages rather than capturing a scroller.
 * - Charts are drawn into a tile from the chart's own canvas, downscaled to print size;
 *   anything else is captured whole with html2canvas as a fallback. A chart that cannot be
 *   copied says so on its tile and in the closing notes, never a silent gap.
 * - Section readings and card commentary are printed in full, continuing on the next page.
 * - Cards keep the board's layout: cards side by side (overlapping vertically on the grid)
 *   form a band. A band that fits on a page is kept whole; a taller one flows row by row, and
 *   a card always starts below whatever ended above it in its column, including a table that
 *   ran on to later pages. A heading is kept with the start of what follows it.
 * - Before saving, every card must have been placed (or listed as not set up), so a partial
 *   PDF is never saved.
 *
 * jsPDF, jspdf-autotable and html2canvas are loaded only when an export is asked for, as the
 * askRichard conversation export loads them, so the Lens page bundle does not grow.
 *
 * The pure helpers (`groupIntoBands`, `pdfSafeText`, `splitMovement`, `isNegativeFigure`,
 * `headerModel`, `captureScale`, `advanceOnPages`, `tableRowNote`, `capLines`,
 * `hasUnsupportedPdfChars`) are unit tested without a DOM or a PDF library.
 */
import { exportFileName } from './lensExport';

export const PDF_PAGE_WIDTH_MM = 210;
export const PDF_PAGE_HEIGHT_MM = 297;
export const PDF_MARGIN_MM = 14;
const CONTENT_WIDTH_MM = PDF_PAGE_WIDTH_MM - PDF_MARGIN_MM * 2;
/** Where content starts on pages after the first, below the running header. */
const CONTENT_TOP_MM = 20;
/** Lowest point content may reach, above the footer. */
const CONTENT_BOTTOM_MM = 282;
const FOOTER_BASELINE_MM = 289;
const BAND_GAP_MM = 4;
const SECTION_GAP_MM = 7;
/** A table starts on the current page only if this much of it (title, header, two rows) fits. */
const TABLE_MIN_START_MM = 24;

/** House palette, taken from the bedbank partnership weekly report. */
type Rgb = readonly [number, number, number];
export const PDF_COLOURS = {
  page: [251, 246, 236] as Rgb,
  band: [11, 79, 92] as Rgb,
  bandSoft: [191, 214, 218] as Rgb,
  rule: [233, 223, 199] as Rgb,
  head: [236, 227, 206] as Rgb,
  ink: [32, 48, 46] as Rgb,
  muted: [107, 107, 99] as Rgb,
  label: [79, 90, 87] as Rgb,
  negative: [178, 58, 46] as Rgb,
  positive: [11, 79, 92] as Rgb,
  tile: [255, 255, 255] as Rgb,
};

const JPEG_QUALITY = 0.92;
/** Canvas limits kept well inside what browsers allow (Chrome caps a side at 32,767px). */
const MAX_CANVAS_SIDE_PX = 16000;
const MAX_CANVAS_AREA_PX = 40_000_000;
const PREFERRED_CAPTURE_SCALE = 2;

/**
 * Marks one section of the board (its heading, narrative and canvas). The section's child that
 * holds the cards is its card grid; every other child is a heading or text.
 */
export const LENS_PDF_SECTION_ATTRIBUTE = 'data-lens-pdf-section';
/** Marks one card on the grid; the value is the card id. */
export const LENS_PDF_CARD_ATTRIBUTE = 'data-lens-pdf-card';
/** The card's type: metric, table, or a chart type. */
export const LENS_PDF_CARD_TYPE_ATTRIBUTE = 'data-lens-pdf-card-type';
/** The card's result status; an `unconfigured` card is listed as not included. */
export const LENS_PDF_CARD_STATUS_ATTRIBUTE = 'data-lens-pdf-card-status';
/** How many rows the card's result holds, so a table can say when it shows fewer. */
export const LENS_PDF_TOTAL_ROWS_ATTRIBUTE = 'data-lens-pdf-total-rows';
/** The card's title, so a typeset card is labelled exactly as on the board. */
export const LENS_PDF_CARD_TITLE_ATTRIBUTE = 'data-lens-pdf-card-title';
/** Anything carrying this attribute is left out of a captured image. */
export const LENS_PDF_IGNORE_ATTRIBUTE = 'data-lens-pdf-ignore';
/** On a card: how many rows it shows after its filters and view (before the 500-row display cap). */
export const LENS_PDF_SHAPED_ROWS_ATTRIBUTE = 'data-lens-pdf-shaped-rows';
/** On a card: the row-count line its footer paints. */
export const LENS_PDF_ROW_NOTE_ATTRIBUTE = 'data-lens-pdf-row-note';
/** On a card: "true" when that line says the card shows only part of its rows. */
export const LENS_PDF_ROW_NOTE_PARTIAL_ATTRIBUTE = 'data-lens-pdf-row-note-partial';
/** On a metric that paints a word instead of a figure ("Partial"). */
export const LENS_PDF_METRIC_FIGURE_ATTRIBUTE = 'data-lens-pdf-metric-figure';
/** On the sentence a metric paints when it has no figure (empty, refused, incomplete). */
export const LENS_PDF_METRIC_MESSAGE_ATTRIBUTE = 'data-lens-pdf-metric-message';
/** On a metric's painted comparison line; the value is its tone (positive, negative, neutral). */
export const LENS_PDF_COMPARISON_ATTRIBUTE = 'data-lens-pdf-comparison';
/** The movement part of that line ("+5.2%"). */
export const LENS_PDF_MOVEMENT_ATTRIBUTE = 'data-lens-pdf-movement';
/** What the movement is against, and any period note, exactly as painted. */
export const LENS_PDF_COMPARISON_DETAIL_ATTRIBUTE = 'data-lens-pdf-comparison-detail';
/** A section's written reading; the value is its provenance line. */
export const LENS_PDF_READING_ATTRIBUTE = 'data-lens-pdf-reading';

export type LensPdfErrorCode = 'busy' | 'empty' | 'missing-cards' | 'stale';

/** A failure the page can explain to the reader in plain words. */
export class LensPdfExportError extends Error {
  constructor(
    readonly code: LensPdfErrorCode,
    message: string,
  ) {
    super(message);
    this.name = 'LensPdfExportError';
  }
}

// ---------------------------------------------------------------------------------------------
// Pure helpers
// ---------------------------------------------------------------------------------------------

/**
 * Groups cards into bands: cards whose vertical extents overlap belong together, so a page
 * break can only fall between bands, never through a card.
 *
 * @returns Groups of indices into `rects`, each sorted left to right, bands top-down.
 */
export function groupIntoBands(
  rects: ReadonlyArray<{ top: number; bottom: number; left?: number }>,
): Array<Array<number>> {
  const order = rects.map((_, index) => index).sort((a, b) => rects[a].top - rects[b].top);
  const bands: Array<{ bottom: number; members: Array<number> }> = [];
  for (const index of order) {
    const rect = rects[index];
    const current = bands[bands.length - 1];
    // Half a pixel of tolerance so rounding at a shared edge does not merge two rows.
    if (current && rect.top < current.bottom - 0.5) {
      current.members.push(index);
      current.bottom = Math.max(current.bottom, rect.bottom);
    } else {
      bands.push({ bottom: rect.bottom, members: [index] });
    }
  }
  return bands.map((band) => band.members.sort((a, b) => (rects[a].left ?? 0) - (rects[b].left ?? 0)));
}

/** The capture scale for an element, kept inside browser canvas limits. */
export function captureScale(widthPx: number, heightPx: number): number {
  if (!(widthPx > 0) || !(heightPx > 0)) {
    return PREFERRED_CAPTURE_SCALE;
  }
  return Math.min(
    PREFERRED_CAPTURE_SCALE,
    MAX_CANVAS_SIDE_PX / widthPx,
    MAX_CANVAS_SIDE_PX / heightPx,
    Math.sqrt(MAX_CANVAS_AREA_PX / (widthPx * heightPx)),
  );
}

/** Characters beyond Latin-1 that the PDF's standard fonts (WinAnsi) can still draw. */
const WIN_ANSI_EXTRA = new Set('€‚ƒ„…†‡ˆ‰Š‹ŒŽ‘’“”•–—˜™š›œžŸ');

/** True for a character the PDF's built-in Helvetica (WinAnsi) can draw. */
export function canHelveticaDraw(char: string): boolean {
  return char.charCodeAt(0) <= 0xff || WIN_ANSI_EXTRA.has(char);
}

/**
 * Lens and `Intl` use a true minus sign and narrow no-break spaces; they are mapped to their
 * plain forms, zero-width characters are dropped, and accents typed as separate marks are
 * joined to their letter, so "Hội An" prints whichever way it was entered.
 */
function normaliseForPdf(text: string): string {
  return text
    .normalize('NFC')
    .replace(/[−‐‑]/g, '-')
    .replace(/[\u00a0\u2007\u2009\u202f]/g, ' ')
    .replace(/[\u200b-\u200d\u2060\ufeff]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * The character itself when the PDF can draw it; otherwise its plain compatibility form when
 * that can be drawn (full-width "（" prints as "("), and "?" when neither can.
 */
function printableForm(char: string, canDraw: (char: string) => boolean): string {
  if (canDraw(char)) {
    return char;
  }
  const plain = char.normalize('NFKC');
  return plain !== char && plain.length > 0 && Array.from(plain).every(canDraw) ? plain : '?';
}

/**
 * Text the PDF can draw. `canDraw` defaults to Helvetica alone; the export passes one that
 * also accepts what its fallback font draws. Anything neither font has prints as "?" rather
 * than as garbage.
 */
export function pdfSafeText(text: string, canDraw: (char: string) => boolean = canHelveticaDraw): string {
  return Array.from(normaliseForPdf(text))
    .map((char) => printableForm(char, canDraw))
    .join('');
}

export type MovementTone = 'positive' | 'negative' | 'neutral';

/** True for a figure the board shows as a fall: "-5.2%", "−A$1.2m", "(1,234)". */
export function isNegativeFigure(text: string): boolean {
  // Only a figure counts: a sign or bracket at the start, or straight after a short currency
  // prefix ("$-1,234", "A$(1,234)": Lens paints a currency cell as two spans, symbol then
  // amount). A name with a dash or bracket in it ("Pullman - 2BR Villa") is not a fall.
  return (
    /^\s*[-−]\s*(?:[A-Za-z]{0,3}[$€£¥]?)\s*\d/.test(text) || /^\s*(?:[A-Za-z]{0,3}[$€£¥])?\s*[-−(]\s*\d/.test(text)
  );
}

/** True when `text` holds characters the PDF cannot draw, so `pdfSafeText` prints "?". */
export function hasUnsupportedPdfChars(text: string, canDraw: (char: string) => boolean = canHelveticaDraw): boolean {
  return Array.from(normaliseForPdf(text)).some((char) => char !== '?' && printableForm(char, canDraw) === '?');
}

export type PagePosition = { page: number; y: number };

/**
 * Where a point `offset` mm below `from` falls when content flows down the page and on to
 * the next, given each page's usable `top` and `bottom`.
 */
export function advanceOnPages(from: PagePosition, offset: number, top: number, bottom: number): PagePosition {
  let { page, y } = from;
  let left = Math.max(0, offset);
  const pageHeight = bottom - top;
  while (y + left > bottom + 0.01 && pageHeight > 0) {
    left -= Math.max(0, bottom - y);
    page += 1;
    y = top;
  }
  return { page, y: y + left };
}

/** The later of two positions on the page flow. */
export function laterPosition(a: PagePosition, b: PagePosition): PagePosition {
  return a.page > b.page || (a.page === b.page && a.y > b.y) ? a : b;
}

/**
 * The note printed above a table that shows fewer rows than the card holds. The board's own
 * footer wording is used whenever the card painted one for a partial view (the 500-row
 * display cap, or a query that hit its row limit); otherwise the count is against the rows
 * the card shows after its filters, never the raw result. `canDraw` is the export's font check,
 * so a painted note keeps characters the fallback font draws.
 */
export function tableRowNote(
  input: {
    shown: number;
    shapedRows?: number;
    paintedNote?: string;
    partial?: boolean;
  },
  canDraw: (char: string) => boolean = canHelveticaDraw,
): string {
  const csv = "Download the card's CSV in Lens for every row.";
  if (input.partial && input.paintedNote) {
    const note = pdfSafeText(input.paintedNote, canDraw).replace(/\.?$/, '.');
    return `${note} ${csv}`;
  }
  if (input.shapedRows !== undefined && Number.isFinite(input.shapedRows) && input.shapedRows > input.shown) {
    return `Showing ${input.shown.toLocaleString('en-AU')} of ${input.shapedRows.toLocaleString('en-AU')} rows, as in Lens. ${csv}`;
  }
  return '';
}

/** Keeps at most `max` lines, ending the last kept line with an ellipsis when cut. */
export function capLines(lines: ReadonlyArray<string>, max: number): Array<string> {
  if (lines.length <= max) {
    return [...lines];
  }
  const kept = lines.slice(0, Math.max(0, max));
  if (kept.length) {
    kept[kept.length - 1] = `${kept[kept.length - 1].replace(/[\s.,;:]+$/, '')}…`;
  }
  return kept;
}

/**
 * Splits a metric comparison as the board shows it ("+5.2% vs 1 Jan – 5 Oct 2025") into the
 * movement and what it is measured against, with the direction for colour.
 */
export function splitMovement(
  value: string,
  canDraw: (char: string) => boolean = canHelveticaDraw,
): { movement: string; against: string; tone: MovementTone } {
  const text = pdfSafeText(value, canDraw);
  const at = text.indexOf(' vs ');
  const movement = at >= 0 ? text.slice(0, at) : text;
  const against = at >= 0 ? text.slice(at + 1) : '';
  const tone: MovementTone = isNegativeFigure(movement)
    ? 'negative'
    : /^\s*\+/.test(movement) && /[1-9]/.test(movement)
      ? 'positive'
      : 'neutral';
  return { against, movement, tone };
}

const GENERATED_FORMAT = new Intl.DateTimeFormat('en-AU', {
  day: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
  month: 'short',
  timeZone: 'Australia/Sydney',
  timeZoneName: 'short',
  year: 'numeric',
});

export type HeaderModel = {
  kicker: string;
  title: string;
  filters: Array<string>;
  periodCurrent?: string;
  periodComparison?: string;
  generated: string;
};

/**
 * What the header band says. The generated time is in Sydney time, the zone the board's
 * periods resolve in, and says so.
 */
export function headerModel(
  input: {
    title: string;
    filters?: ReadonlyArray<string>;
    periodCurrent?: string;
    periodComparison?: string;
    periodLabel?: string;
    generatedAt?: Date;
  },
  canDraw: (char: string) => boolean = canHelveticaDraw,
): HeaderModel {
  const clean = (text: string) => pdfSafeText(text, canDraw);
  let periodCurrent = input.periodCurrent;
  let periodComparison = input.periodComparison;
  if (!periodCurrent && input.periodLabel) {
    const [current, ...rest] = input.periodLabel.split(' vs ');
    periodCurrent = current;
    periodComparison = rest.length ? rest.join(' vs ') : undefined;
  }
  return {
    filters: (input.filters ?? []).map(clean).filter(Boolean),
    generated: `Generated ${pdfSafeText(GENERATED_FORMAT.format(input.generatedAt ?? new Date()))}`,
    kicker: 'LUXURY ESCAPES | LENS REPORT',
    periodComparison: periodComparison ? clean(periodComparison) : undefined,
    periodCurrent: periodCurrent ? clean(periodCurrent) : undefined,
    title: clean(input.title) || 'Lens',
  };
}

// ---------------------------------------------------------------------------------------------
// Reading the board
// ---------------------------------------------------------------------------------------------

type Wand = { kind: string; label: string; value: string };

function wandsIn(element: Element): Array<Wand> {
  return Array.from(element.querySelectorAll('[data-lens-wand]')).flatMap((node) => {
    try {
      const parsed = JSON.parse(node.getAttribute('data-lens-wand') ?? '') as Partial<Wand>;
      return parsed && typeof parsed.kind === 'string'
        ? [{ kind: parsed.kind, label: String(parsed.label ?? ''), value: String(parsed.value ?? '') }]
        : [];
    } catch {
      return [];
    }
  });
}

type TableModel = {
  head: Array<string>;
  body: Array<Array<string>>;
  align: Array<'left' | 'right'>;
};

function readTable(element: HTMLElement, clean: (text: string) => string = pdfSafeText): TableModel | undefined {
  const table = element.querySelector('table');
  if (!table) {
    return undefined;
  }
  const headCells = Array.from(table.querySelectorAll('thead tr:last-child th, thead tr:last-child td'));
  const rows = Array.from(table.querySelectorAll('tbody tr'));
  const firstRow = rows[0] ? Array.from(rows[0].querySelectorAll('td, th')) : [];
  const alignOf = (cell: Element | undefined): 'left' | 'right' =>
    cell && getComputedStyle(cell).textAlign === 'right' ? 'right' : 'left';
  const width = Math.max(headCells.length, firstRow.length);
  return {
    align: Array.from({ length: width }, (_, index) => alignOf(firstRow[index] ?? headCells[index])),
    body: rows.map((row) => Array.from(row.querySelectorAll('td, th')).map((cell) => clean(cell.textContent ?? ''))),
    head: headCells.map((cell) => clean(cell.textContent ?? '')),
  };
}

type InsightBlock = { text: string; heading?: boolean };

/** Read all rendered prose, including text below a card's scroll viewport and scope notices. */
function readInsight(element: HTMLElement, clean: (text: string) => string): Array<InsightBlock> {
  const content = element.querySelector('[data-lens-insight-content]');
  const blocks: Array<InsightBlock> = [];
  const add = (text: string, heading = false) => {
    const value = clean(text);
    if (value) {
      blocks.push({ text: value, heading });
    }
  };
  const textOf = (node: Node, omitNestedLists = false): string => {
    if (node.nodeType === Node.TEXT_NODE) {
      return node.textContent ?? '';
    }
    if (node instanceof Element) {
      if (omitNestedLists && ['UL', 'OL'].includes(node.tagName)) {
        return '';
      }
      if (node.tagName === 'BR') {
        return '\n';
      }
      if (node.tagName === 'IMG') {
        return node.getAttribute('alt') ?? '';
      }
    }
    const text = Array.from(node.childNodes)
      .map((child) => textOf(child, omitNestedLists))
      .join('');
    return node instanceof Element && ['P', 'PRE', 'LI'].includes(node.tagName) ? `${text}\n` : text;
  };
  const visit = (node: Node) => {
    if (!(node instanceof Element)) {
      add(node.textContent ?? '');
      return;
    }
    if (node.hasAttribute(LENS_PDF_IGNORE_ATTRIBUTE) || ['SCRIPT', 'STYLE', 'BUTTON'].includes(node.tagName)) {
      return;
    }
    if (node.tagName === 'LI') {
      const ordered = node.parentElement?.tagName === 'OL';
      const start = Number(node.parentElement?.getAttribute('start')) || 1;
      const index = Array.from(node.parentElement?.children ?? []).indexOf(node) + start;
      add(`${ordered ? `${index}.` : '•'} ${textOf(node, true)}`);
      Array.from(node.children)
        .filter((child) => ['UL', 'OL'].includes(child.tagName))
        .forEach(visit);
    } else if (node.tagName === 'TR') {
      add(
        Array.from(node.children)
          .map((cell) => textOf(cell))
          .join(' | '),
      );
    } else if (/^H[1-6]$/.test(node.tagName) || ['P', 'PRE', 'BLOCKQUOTE'].includes(node.tagName)) {
      add(textOf(node), /^H[1-6]$/.test(node.tagName));
    } else {
      Array.from(node.childNodes).forEach(visit);
    }
  };
  if (content) {
    Array.from(content.childNodes).forEach(visit);
  }
  return blocks;
}

/** The element a chart is drawn into, or undefined when the card has no chart. */
function chartHost(element: HTMLElement): HTMLElement | undefined {
  return (
    element.querySelector<HTMLElement>('[data-lens-chart]') ??
    element.querySelector<HTMLElement>('.recharts-wrapper') ??
    undefined
  );
}

function isDark(colour: string): boolean {
  const match = colour.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*([\d.]+))?/);
  if (!match || (match[4] !== undefined && Number(match[4]) === 0)) {
    return false;
  }
  const [r, g, b] = [Number(match[1]), Number(match[2]), Number(match[3])];
  return 0.2126 * r + 0.7152 * g + 0.0722 * b < 128;
}

function rgbOf(colour: string): Rgb {
  const match = colour.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/);
  return match ? [Number(match[1]), Number(match[2]), Number(match[3])] : PDF_COLOURS.tile;
}

// ---------------------------------------------------------------------------------------------
// The export
// ---------------------------------------------------------------------------------------------

export type ExportLensBoardPdfInput = {
  /** The board root: the element that wraps every section of the canvas. */
  root: HTMLElement;
  title: string;
  /** The Lens description, printed under the header band when there is one. */
  description?: string | null;
  /** The resolved period as the board labels it, and what it is compared against. */
  periodCurrent?: string;
  periodComparison?: string;
  /** "current vs comparison" in one string; used when the parts are not given. */
  periodLabel?: string;
  /** Human-readable active filters, e.g. "Partner: Accor". */
  filters?: ReadonlyArray<string>;
  /** How many cards the board renders. The export fails rather than ship a PDF missing any. */
  expectedCardCount: number;
  /** True once the board has changed since the export started; the export then stops. */
  isStale?: () => boolean;
  onProgress?: (done: number, total: number) => void;
  generatedAt?: Date;
  /** Receives the finished PDF instead of the browser saving it (the scheduled-email print page). */
  deliver?: (pdf: ArrayBuffer) => void;
};

/** What was saved, and what the reader should be told the PDF could not carry. */
export type LensPdfExportResult = {
  pageCount: number;
  /** Titles of charts that could not be copied (each says so in the PDF). */
  missingCharts: Array<string>;
  /** True when some text used characters the PDF font cannot draw (printed as "?"). */
  substitutedCharacters: boolean;
};

type CardItem = {
  element: HTMLElement;
  kind: 'metric' | 'table' | 'chart' | 'insight' | 'other';
  title: string;
  rect: DOMRect;
};

type FallbackFont = {
  family: string;
  covers: (char: string) => boolean;
  regular: string;
  bold: string;
};

/**
 * The fallback font for names Helvetica cannot draw (./pdfFont), a separate chunk fetched when
 * a PDF is made. If it cannot be loaded the export still runs, printing those characters as
 * "?" and saying so, as it did before the font existed.
 */
async function loadFallbackFont(): Promise<FallbackFont | undefined> {
  try {
    const font = await import('./pdfFont/lensPdfNotoSans');
    return {
      bold: font.LENS_PDF_FALLBACK_BOLD_TTF,
      covers: (char) => {
        const code = char.codePointAt(0) ?? 0;
        return font.LENS_PDF_FALLBACK_RANGES.some(([first, last]) => code >= first && code <= last);
      },
      family: font.LENS_PDF_FALLBACK_FAMILY,
      regular: font.LENS_PDF_FALLBACK_REGULAR_TTF,
    };
  } catch {
    return undefined;
  }
}

/** Chart and capture images are stored at about 150 dpi: sharp in print, not 20 MB. */
const IMAGE_PX_PER_MM = 6;
const TEXT_LINE_MM = 3.2;

/**
 * Typesets the board and downloads it as an A4 PDF. Throws a `LensPdfExportError` when the
 * board changes mid-export, has nothing to export, or a card cannot be placed in the PDF.
 */
export async function exportLensBoardPdf(input: ExportLensBoardPdfInput): Promise<LensPdfExportResult> {
  const [{ default: JsPdf }, autoTableModule, fallbackFont] = await Promise.all([
    import('jspdf'),
    import('jspdf-autotable'),
    loadFallbackFont(),
  ]);
  type Pdf = InstanceType<typeof JsPdf>;
  const autoTable = (autoTableModule.default ?? autoTableModule.autoTable) as (
    doc: Pdf,
    options: Record<string, unknown>,
  ) => void;
  let html2canvasLoaded: typeof import('html2canvas').default | undefined;
  const html2canvas = async () => {
    html2canvasLoaded ??= (await import('html2canvas')).default;
    return html2canvasLoaded;
  };
  const checkStale = () => {
    if (input.isStale?.()) {
      throw new LensPdfExportError('stale', 'The board changed while the PDF was being made.');
    }
  };

  // --- Read the board structure before drawing anything --------------------------------------
  const sections = Array.from(input.root.querySelectorAll<HTMLElement>(`[${LENS_PDF_SECTION_ATTRIBUTE}]`));
  const allCards = Array.from(input.root.querySelectorAll<HTMLElement>(`[${LENS_PDF_CARD_ATTRIBUTE}]`));
  if (!allCards.length) {
    throw new LensPdfExportError('empty', 'There are no cards on the board to export.');
  }
  if (allCards.length < input.expectedCardCount) {
    throw new LensPdfExportError(
      'missing-cards',
      `Only ${allCards.length} of ${input.expectedCardCount} cards are on the board.`,
    );
  }
  const notIncluded: Array<string> = [];
  const missingCharts: Array<string> = [];
  /** What the PDF can draw: Helvetica, and the fallback font when it loaded. */
  const canDraw = (char: string) => canHelveticaDraw(char) || Boolean(fallbackFont?.covers(char));
  // Only text that is printed counts: every string goes through `safe` on its way to the PDF.
  let substitutedCharacters = hasUnsupportedPdfChars(
    [
      input.title,
      input.description ?? '',
      ...(input.filters ?? []),
      input.periodCurrent ?? '',
      input.periodComparison ?? '',
      input.periodLabel ?? '',
    ].join(' '),
    canDraw,
  );
  const safe = (text: string) => {
    if (!substitutedCharacters && hasUnsupportedPdfChars(text, canDraw)) {
      substitutedCharacters = true;
    }
    return pdfSafeText(text, canDraw);
  };
  const titleOf = (card: HTMLElement) =>
    safe(
      card.getAttribute(LENS_PDF_CARD_TITLE_ATTRIBUTE) ??
        card.querySelector('.lens-card-heading')?.textContent ??
        wandsIn(card).find((wand) => wand.kind === 'metric')?.label ??
        '',
    );
  const total = allCards.length;
  const handled = new Set<HTMLElement>();
  const step = (card: HTMLElement) => {
    handled.add(card);
    input.onProgress?.(handled.size, total);
  };

  // --- Document and page furniture -----------------------------------------------------------
  // Compressed: jsPDF otherwise stores chart images raw, which made a 7-page report 20+ MB.
  const pdf: Pdf = new JsPdf({ compress: true, format: 'a4', orientation: 'p', unit: 'mm' });
  const header = headerModel(input, canDraw);

  // Text Helvetica cannot draw is measured and drawn in the fallback font, in the same weight.
  // `safe` has already turned anything neither font has into "?". Wrapping the three text calls
  // here covers every drawing site; table cells switch font in `didParseCell` below. The font
  // is registered on first use, so a report without such names is unchanged.
  let fallbackRegistered = false;
  const fallbackFamily = () => {
    if (!fallbackFont) {
      return undefined;
    }
    if (!fallbackRegistered) {
      pdf.addFileToVFS('LensPdfNotoSans-Regular.ttf', fallbackFont.regular);
      pdf.addFont('LensPdfNotoSans-Regular.ttf', fallbackFont.family, 'normal');
      pdf.addFileToVFS('LensPdfNotoSans-Bold.ttf', fallbackFont.bold);
      pdf.addFont('LensPdfNotoSans-Bold.ttf', fallbackFont.family, 'bold');
      fallbackRegistered = true;
    }
    return fallbackFont.family;
  };
  const needsFallback = (value: unknown) =>
    Boolean(fallbackFont) &&
    (Array.isArray(value) ? value : [value]).some(
      (part) => typeof part === 'string' && Array.from(part).some((char) => !canHelveticaDraw(char)),
    );
  if (fallbackFont) {
    const inFallback =
      <Args extends [unknown, ...Array<unknown>], Result>(method: (...args: Args) => Result) =>
      (...args: Args): Result => {
        const current = pdf.getFont();
        const family = current.fontName === 'helvetica' && needsFallback(args[0]) ? fallbackFamily() : undefined;
        if (!family) {
          return method(...args);
        }
        pdf.setFont(family, /bold/.test(current.fontStyle) ? 'bold' : 'normal');
        try {
          return method(...args);
        } finally {
          pdf.setFont(current.fontName, current.fontStyle);
        }
      };
    pdf.text = inFallback(pdf.text.bind(pdf));
    pdf.splitTextToSize = inFallback(pdf.splitTextToSize.bind(pdf));
    pdf.getTextWidth = inFallback(pdf.getTextWidth.bind(pdf));
  }
  const decorated = new Set<number>();
  const fill = (rgb: Rgb) => pdf.setFillColor(rgb[0], rgb[1], rgb[2]);
  const stroke = (rgb: Rgb) => pdf.setDrawColor(rgb[0], rgb[1], rgb[2]);
  const ink = (rgb: Rgb) => pdf.setTextColor(rgb[0], rgb[1], rgb[2]);
  const font = (style: 'normal' | 'bold' | 'italic', size: number) => {
    pdf.setFont('helvetica', style);
    pdf.setFontSize(size);
  };
  const pageNumber = () => pdf.getCurrentPageInfo().pageNumber;
  const contentHeight = CONTENT_BOTTOM_MM - CONTENT_TOP_MM;
  /** The tallest a card is drawn: a page less room for a section heading above it. */
  const cardMaxHeight = contentHeight - 24;

  /** Cream page and, after page one, a running header. Safe to call more than once. */
  const decorate = (page: number) => {
    if (decorated.has(page)) {
      return;
    }
    decorated.add(page);
    const previous = pageNumber();
    pdf.setPage(page);
    fill(PDF_COLOURS.page);
    pdf.rect(0, 0, PDF_PAGE_WIDTH_MM, PDF_PAGE_HEIGHT_MM, 'F');
    if (page > 1) {
      font('bold', 7.5);
      ink(PDF_COLOURS.label);
      pdf.text(truncateToWidth(pdf, header.title.toUpperCase(), CONTENT_WIDTH_MM * 0.6), PDF_MARGIN_MM, 12);
      if (header.periodCurrent) {
        font('normal', 7.5);
        pdf.text(
          truncateToWidth(pdf, header.periodCurrent, CONTENT_WIDTH_MM * 0.38),
          PDF_PAGE_WIDTH_MM - PDF_MARGIN_MM,
          12,
          { align: 'right' },
        );
      }
      stroke(PDF_COLOURS.rule);
      pdf.setLineWidth(0.3);
      pdf.line(PDF_MARGIN_MM, 14.5, PDF_PAGE_WIDTH_MM - PDF_MARGIN_MM, 14.5);
    }
    pdf.setPage(previous);
  };

  /** Makes `page` current, adding decorated pages up to it when it does not exist yet. */
  const goTo = (page: number) => {
    while (pdf.getNumberOfPages() < page) {
      pdf.addPage();
      decorate(pdf.getNumberOfPages());
    }
    pdf.setPage(page);
  };

  const cursor: PagePosition = { page: 1, y: 0 };
  let firstPageTop = CONTENT_TOP_MM;
  const topOf = (page: number) => (page === 1 ? firstPageTop : CONTENT_TOP_MM);
  const newPage = () => {
    goTo(cursor.page + 1);
    cursor.page = pageNumber();
    cursor.y = CONTENT_TOP_MM;
  };
  /** Moves to a fresh page unless `need` mm fits below the cursor or the page is still empty. */
  const ensureSpace = (need: number) => {
    if (cursor.y + need > CONTENT_BOTTOM_MM && cursor.y > topOf(cursor.page) + 0.01) {
      newPage();
    }
  };
  /**
   * Writes lines at the cursor, one at a time, so a long passage continues on the next page.
   * `style` sets the font and colour again after a page break.
   */
  const flowLines = (
    lines: ReadonlyArray<string>,
    lineHeight: number,
    style: () => void,
    options: { x?: number; bar?: Rgb } = {},
  ) => {
    const x = options.x ?? PDF_MARGIN_MM;
    for (const line of lines) {
      ensureSpace(lineHeight);
      goTo(cursor.page);
      if (options.bar) {
        fill(options.bar);
        pdf.rect(PDF_MARGIN_MM, cursor.y, 0.7, lineHeight, 'F');
      }
      style();
      pdf.text(line, x, cursor.y + lineHeight * 0.75);
      cursor.y += lineHeight;
    }
  };

  // --- Page one: header band, then anything too long for it ----------------------------------
  decorate(1);
  const bandX = PDF_MARGIN_MM;
  const bandY = 10;
  const bandW = CONTENT_WIDTH_MM;
  const leftW = bandW * 0.6 - 8;
  const rightW = bandW * 0.4 - 8;
  // Measure first, then draw, so the band is exactly as tall as what it holds. The band is
  // bounded: a long title or filter list is shortened in the band and printed in full below.
  font('bold', 20);
  const allTitleLines = pdf.splitTextToSize(header.title, leftW) as Array<string>;
  const titleLines = capLines(allTitleLines, 3);
  font('normal', 9.5);
  const allFilterLines = header.filters.length
    ? (pdf.splitTextToSize(header.filters.join('  ·  '), leftW) as Array<string>)
    : [];
  const filtersOverflow = allFilterLines.length > 3;
  const filterLines = filtersOverflow
    ? [...capLines(allFilterLines, 2), 'All filters are listed below.']
    : allFilterLines;
  font('normal', 11);
  const periodLines = header.periodCurrent
    ? capLines(pdf.splitTextToSize(header.periodCurrent, rightW) as Array<string>, 2)
    : [];
  font('normal', 8);
  const comparisonLines = header.periodComparison
    ? capLines(pdf.splitTextToSize(`vs ${header.periodComparison}`, rightW) as Array<string>, 2)
    : [];
  const leftBottom = 17 + titleLines.length * 8 + (filterLines.length ? 1 + filterLines.length * 4.4 : 0);
  const rightBottom = 13 + periodLines.length * 4.8 + comparisonLines.length * 3.8 + 5;
  const bandH = Math.max(36, leftBottom + 6, rightBottom + 6);

  fill(PDF_COLOURS.band);
  pdf.roundedRect(bandX, bandY, bandW, bandH, 4, 4, 'F');
  ink([255, 255, 255]);
  font('bold', 7.5);
  pdf.text(header.kicker, bandX + 7, bandY + 8);
  font('bold', 20);
  pdf.text(titleLines, bandX + 7, bandY + 18, { lineHeightFactor: 1.12 });
  if (filterLines.length) {
    font('normal', 9.5);
    ink(PDF_COLOURS.bandSoft);
    pdf.text(filterLines, bandX + 7, bandY + 17 + titleLines.length * 8 + 1.5, { lineHeightFactor: 1.3 });
  }
  const rightX = bandX + bandW - 7;
  ink([255, 255, 255]);
  font('bold', 7.5);
  pdf.text('REPORTING PERIOD', rightX, bandY + 8, { align: 'right' });
  if (periodLines.length) {
    font('normal', 11);
    pdf.text(periodLines, rightX, bandY + 14, { align: 'right', lineHeightFactor: 1.2 });
  }
  if (comparisonLines.length) {
    font('normal', 8);
    ink(PDF_COLOURS.bandSoft);
    pdf.text(comparisonLines, rightX, bandY + 14 + periodLines.length * 4.8, {
      align: 'right',
      lineHeightFactor: 1.3,
    });
  }
  font('normal', 7);
  ink(PDF_COLOURS.bandSoft);
  pdf.text(header.generated, rightX, bandY + bandH - 5, { align: 'right' });

  firstPageTop = bandY + bandH + 6;
  cursor.y = firstPageTop + 1;
  if (allTitleLines.length > titleLines.length) {
    font('bold', 10);
    flowLines(pdf.splitTextToSize(header.title, CONTENT_WIDTH_MM) as Array<string>, 4.6, () => {
      font('bold', 10);
      ink(PDF_COLOURS.ink);
    });
    cursor.y += 1.5;
  }
  if (filtersOverflow) {
    font('normal', 8.5);
    flowLines(
      pdf.splitTextToSize(`Filters: ${header.filters.join('  ·  ')}`, CONTENT_WIDTH_MM) as Array<string>,
      3.9,
      () => {
        font('normal', 8.5);
        ink(PDF_COLOURS.label);
      },
    );
    cursor.y += 1.5;
  }
  const description = input.description ? safe(input.description) : '';
  if (description) {
    font('normal', 9);
    flowLines(pdf.splitTextToSize(description, CONTENT_WIDTH_MM) as Array<string>, 4.3, () => {
      font('normal', 9);
      ink(PDF_COLOURS.ink);
    });
    cursor.y += 3;
  }

  // --- Drawing primitives ----------------------------------------------------------------------
  const tileFrame = (x: number, y: number, w: number, h: number, background: Rgb = PDF_COLOURS.tile) => {
    fill(background);
    stroke(PDF_COLOURS.rule);
    pdf.setLineWidth(0.3);
    pdf.roundedRect(x, y, w, h, 1.6, 1.6, 'FD');
  };

  /** A card's written commentary (its reading), as the card shows it. */
  const commentaryOf = (card: HTMLElement) =>
    wandsIn(card)
      .filter((wand) => wand.kind === 'text' && wand.value)
      .map((wand) => safe(wand.value))
      .join(' ');

  /** The card's painted row-count line, when it says the card shows only part of its rows. */
  const partialRowNote = (card: HTMLElement) => {
    const marked = card.querySelector(`[${LENS_PDF_ROW_NOTE_ATTRIBUTE}]`) ?? card;
    return marked.getAttribute(LENS_PDF_ROW_NOTE_PARTIAL_ATTRIBUTE) === 'true'
      ? safe(marked.getAttribute(LENS_PDF_ROW_NOTE_ATTRIBUTE) ?? '')
      : '';
  };

  /** What a KPI tile prints, and the height it needs at width `w`. */
  const metricLayout = (card: HTMLElement, title: string, w: number) => {
    const wands = wandsIn(card);
    const metric = wands.find((wand) => wand.kind === 'metric');
    const inner = w - 7;
    font('bold', 6.5);
    const label = capLines(pdf.splitTextToSize(title.toUpperCase(), inner) as Array<string>, 3);
    // A card with no figure paints a word ("Partial") or a sentence instead; print those.
    const figure = safe(card.querySelector(`[${LENS_PDF_METRIC_FIGURE_ATTRIBUTE}]`)?.textContent ?? '');
    const value = metric ? safe(metric.value) : figure || '–';
    let valueSize = 15;
    font('bold', valueSize);
    while (valueSize > 9 && pdf.getTextWidth(value) > inner) {
      valueSize -= 0.5;
      font('bold', valueSize);
    }
    // The comparison as painted (movement, what it is against, a period note), falling back
    // to the wand target when a card does not mark its painted line.
    let movement: { movement: string; against: string; tone: MovementTone } | undefined;
    const painted = card.querySelector(`[${LENS_PDF_COMPARISON_ATTRIBUTE}]`);
    if (painted) {
      const moved = safe(painted.querySelector(`[${LENS_PDF_MOVEMENT_ATTRIBUTE}]`)?.textContent ?? '');
      const detail = safe(painted.querySelector(`[${LENS_PDF_COMPARISON_DETAIL_ATTRIBUTE}]`)?.textContent ?? '');
      const toneAttribute = painted.getAttribute(LENS_PDF_COMPARISON_ATTRIBUTE);
      const tone: MovementTone =
        toneAttribute === 'positive' || toneAttribute === 'negative' || toneAttribute === 'neutral'
          ? toneAttribute
          : moved
            ? splitMovement(moved, canDraw).tone
            : 'neutral';
      movement = moved || detail ? { against: detail, movement: moved, tone } : undefined;
    } else {
      const comparison = wands.find((wand) => wand.kind === 'comparison');
      movement = comparison ? splitMovement(safe(comparison.value), canDraw) : undefined;
    }
    font('bold', 7);
    const movementWidth = movement?.movement ? pdf.getTextWidth(movement.movement) + 1 : 0;
    font('normal', 7);
    const againstLines = movement?.against
      ? capLines(pdf.splitTextToSize(movement.against, Math.max(10, inner - movementWidth)) as Array<string>, 3)
      : [];
    const messageText = metric
      ? ''
      : [
          safe(card.querySelector(`[${LENS_PDF_METRIC_MESSAGE_ATTRIBUTE}]`)?.textContent ?? '') ||
            'No figure shown in Lens',
          partialRowNote(card),
        ]
          .filter(Boolean)
          .join(' ');
    const messageLines = messageText ? (pdf.splitTextToSize(messageText, inner) as Array<string>) : [];
    font('normal', 6.8);
    const commentary = commentaryOf(card);
    const commentLines = commentary ? (pdf.splitTextToSize(commentary, inner) as Array<string>) : [];
    const height =
      4 +
      label.length * 2.7 +
      2 +
      valueSize * 0.42 +
      2 +
      (movement ? 3.4 * Math.max(1, againstLines.length) : 0) +
      messageLines.length * TEXT_LINE_MM +
      (commentLines.length ? 1 + commentLines.length * 3 : 0) +
      3.5;
    return {
      againstLines,
      commentLines,
      hasFigure: Boolean(metric),
      height,
      label,
      messageLines,
      movement,
      movementWidth,
      value,
      valueSize,
    };
  };

  const drawMetric = (layout: ReturnType<typeof metricLayout>, x: number, y: number, w: number, h: number) => {
    tileFrame(x, y, w, h);
    let at = y + 5.5;
    font('bold', 6.5);
    ink(PDF_COLOURS.label);
    pdf.text(layout.label, x + 3.5, at, { lineHeightFactor: 1.15 });
    at += (layout.label.length - 1) * 2.7 + 2 + layout.valueSize * 0.42;
    font('bold', layout.valueSize);
    ink(layout.hasFigure ? PDF_COLOURS.ink : PDF_COLOURS.muted);
    pdf.text(layout.value, x + 3.5, at);
    at += 4.6;
    if (layout.movement) {
      const { movement, tone } = layout.movement;
      if (movement) {
        ink(
          tone === 'negative' ? PDF_COLOURS.negative : tone === 'positive' ? PDF_COLOURS.positive : PDF_COLOURS.muted,
        );
        font('bold', 7);
        pdf.text(movement, x + 3.5, at);
      }
      font('normal', 7);
      ink(PDF_COLOURS.muted);
      if (layout.againstLines.length) {
        pdf.text(layout.againstLines, x + 3.5 + layout.movementWidth, at, { lineHeightFactor: 1.35 });
      }
      at += 3.4 * Math.max(1, layout.againstLines.length);
    }
    const startPage = pageNumber();
    if (layout.height > h) {
      // A metric's written context can exceed its capped tile. Continue in its column,
      // and return the real end so later cards cannot cover that continuation.
      let end = flowColumnLines({ page: startPage, y: at }, layout.messageLines, x + 3.5, () => {
        font('normal', 7);
        ink(PDF_COLOURS.muted);
      });
      if (layout.commentLines.length) {
        end = flowColumnLines({ page: end.page, y: end.y + 1 }, layout.commentLines, x + 3.5, () => {
          font('normal', 6.8);
          ink(PDF_COLOURS.ink);
        });
      }
      return laterPosition({ page: startPage, y: y + h }, { page: end.page, y: end.y + 3.5 });
    }
    if (layout.messageLines.length) {
      font('normal', 7);
      ink(PDF_COLOURS.muted);
      pdf.text(layout.messageLines, x + 3.5, at, { lineHeightFactor: 1.3 });
      at += layout.messageLines.length * TEXT_LINE_MM;
    }
    if (layout.commentLines.length) {
      font('normal', 6.8);
      ink(PDF_COLOURS.ink);
      pdf.text(layout.commentLines, x + 3.5, at + 1, { lineHeightFactor: 1.3 });
    }
    return { page: startPage, y: y + h };
  };

  /** A PNG of `source` no larger than `widthMm` needs in print. */
  const printImage = (source: HTMLCanvasElement, widthMm: number) => {
    const target = Math.max(1, Math.round(widthMm * IMAGE_PX_PER_MM));
    if (source.width <= target * 1.05) {
      return source.toDataURL('image/png');
    }
    const scaled = document.createElement('canvas');
    scaled.width = target;
    scaled.height = Math.max(1, Math.round((source.height * target) / source.width));
    const context = scaled.getContext('2d');
    if (!context) {
      return source.toDataURL('image/png');
    }
    context.imageSmoothingEnabled = true;
    context.imageSmoothingQuality = 'high';
    context.drawImage(source, 0, 0, scaled.width, scaled.height);
    const data = scaled.toDataURL('image/png');
    scaled.width = 0;
    scaled.height = 0;
    return data;
  };

  /** An image for a card's chart: the chart's own canvas when it is one, else a capture. */
  const chartImage = async (card: HTMLElement, widthMm: number) => {
    const host = chartHost(card);
    const target = host ?? card;
    const canvases = host ? Array.from(host.querySelectorAll('canvas')).filter((c) => c.width > 0 && c.height > 0) : [];
    if (canvases.length === 1) {
      try {
        const canvas = canvases[0];
        const box = canvas.getBoundingClientRect();
        if (box.width > 0 && box.height > 0) {
          return { data: printImage(canvas, widthMm), heightPx: box.height, widthPx: box.width };
        }
      } catch {
        // A tainted canvas cannot be read directly; fall through to a capture.
      }
    }
    const rect = target.getBoundingClientRect();
    if (!(rect.width > 0) || !(rect.height > 0)) {
      return undefined;
    }
    try {
      const canvas = await (
        await html2canvas()
      )(target, {
        backgroundColor: null,
        ignoreElements: (node: Element) =>
          node.hasAttribute?.(LENS_PDF_IGNORE_ATTRIBUTE) || node.getAttribute?.('data-testid') === 'card-action-tray',
        logging: false,
        scale: captureScale(rect.width, rect.height),
        useCORS: true,
      });
      if (!canvas.width || !canvas.height) {
        return undefined;
      }
      const data = printImage(canvas, widthMm);
      canvas.width = 0;
      canvas.height = 0;
      return { data, heightPx: rect.height, widthPx: rect.width };
    } catch {
      return undefined;
    }
  };

  /** What a chart tile prints: its title, the plot, and all of its commentary. */
  const chartLayout = (item: CardItem, w: number, boardH: number) => {
    font('bold', 8.5);
    const titleLines = capLines(pdf.splitTextToSize(item.title, w - 7) as Array<string>, 2);
    font('normal', 7);
    const commentary = commentaryOf(item.element);
    let noteLines = commentary ? (pdf.splitTextToSize(commentary, w - 7) as Array<string>) : [];
    const titleH = 5.5 + titleLines.length * 3.6 + 1;
    const minPlot = 30;
    let h = Math.max(boardH, titleH + minPlot + noteLines.length * 3.1 + 3);
    if (h > cardMaxHeight) {
      // Commentary longer than a page beside its chart is cut, and says it continues in Lens.
      const room = Math.floor((cardMaxHeight - titleH - minPlot - 3) / 3.1) - 1;
      noteLines = [...capLines(noteLines, Math.max(1, room)), 'The full commentary is on the card in Lens.'];
      h = cardMaxHeight;
    }
    return { h, noteLines, titleLines };
  };

  const drawChart = async (
    item: CardItem,
    layout: ReturnType<typeof chartLayout>,
    x: number,
    y: number,
    w: number,
    h: number,
  ) => {
    const shell = item.element.querySelector<HTMLElement>('.MuiCard-root') ?? item.element;
    const background = getComputedStyle(shell).backgroundColor;
    const dark = isDark(background);
    tileFrame(x, y, w, h, dark ? rgbOf(background) : PDF_COLOURS.tile);
    font('bold', 8.5);
    ink(dark ? [240, 240, 240] : PDF_COLOURS.ink);
    pdf.text(layout.titleLines, x + 3.5, y + 5.5, { lineHeightFactor: 1.2 });
    const top = y + 5.5 + layout.titleLines.length * 3.6 + 1;
    const bottom = y + h - 3 - layout.noteLines.length * 3.1;
    const image = bottom - top > 5 ? await chartImage(item.element, w - 6) : undefined;
    if (image) {
      const boxW = w - 6;
      const boxH = bottom - top;
      const ratio = image.widthPx / image.heightPx;
      const drawW = Math.min(boxW, boxH * ratio);
      const drawH = drawW / ratio;
      pdf.addImage(
        image.data,
        'PNG',
        x + 3 + (boxW - drawW) / 2,
        top + (boxH - drawH) / 2,
        drawW,
        drawH,
        undefined,
        'FAST',
      );
    } else {
      // Never a silent gap: the tile says what is missing, and the export reports it.
      missingCharts.push(item.title || 'Untitled chart');
      font('italic', 7.5);
      ink(dark ? [220, 220, 220] : PDF_COLOURS.muted);
      pdf.text(
        pdf.splitTextToSize(
          'This chart could not be copied into the PDF. Open the Lens to see it.',
          w - 10,
        ) as Array<string>,
        x + w / 2,
        top + Math.max(4, (bottom - top) / 2),
        { align: 'center', lineHeightFactor: 1.3 },
      );
    }
    if (layout.noteLines.length) {
      font('normal', 7);
      ink(dark ? [220, 220, 220] : PDF_COLOURS.muted);
      pdf.text(layout.noteLines, x + 3.5, y + h - 3 - (layout.noteLines.length - 1) * 3.1, { lineHeightFactor: 1.3 });
    }
  };

  /** A card nothing else can typeset, captured whole. */
  const drawCapture = async (item: CardItem, x: number, y: number, w: number, h: number) => {
    const rect = item.element.getBoundingClientRect();
    const canvas = await (
      await html2canvas()
    )(item.element, {
      backgroundColor: '#ffffff',
      ignoreElements: (node: Element) =>
        node.hasAttribute?.(LENS_PDF_IGNORE_ATTRIBUTE) || node.getAttribute?.('data-testid') === 'card-action-tray',
      logging: false,
      scale: captureScale(rect.width, rect.height),
      useCORS: true,
    });
    if (!canvas.width || !canvas.height) {
      throw new LensPdfExportError('empty', `"${item.title}" could not be captured.`);
    }
    const ratio = rect.width / rect.height;
    const drawW = Math.min(w, h * ratio);
    const target = document.createElement('canvas');
    target.width = Math.max(1, Math.round(drawW * IMAGE_PX_PER_MM));
    target.height = Math.max(1, Math.round(target.width / ratio));
    target.getContext('2d')?.drawImage(canvas, 0, 0, target.width, target.height);
    const data = target.toDataURL('image/jpeg', JPEG_QUALITY);
    canvas.width = 0;
    canvas.height = 0;
    target.width = 0;
    target.height = 0;
    pdf.addImage(data, 'JPEG', x, y, drawW, drawW / ratio, undefined, 'FAST');
  };

  /** Writes lines in the column [x, x + w] from `from`, continuing on the next page; returns the end. */
  const flowColumnLines = (
    from: PagePosition,
    lines: ReadonlyArray<string>,
    x: number,
    style: () => void,
    lineHeight = TEXT_LINE_MM,
  ) => {
    let at = { ...from };
    for (const line of lines) {
      if (at.y + lineHeight > CONTENT_BOTTOM_MM) {
        at = { page: at.page + 1, y: CONTENT_TOP_MM };
      }
      goTo(at.page);
      style();
      pdf.text(line, x, at.y + lineHeight * 0.75);
      at.y += lineHeight;
    }
    return at;
  };

  /** Insights are prose, not query results or a screenshot of a scrollable card. */
  const drawInsight = (item: CardItem, from: PagePosition, x: number, w: number): PagePosition => {
    font('bold', 9.5);
    const titleLines = pdf.splitTextToSize(safe(item.title), w) as Array<string>;
    let at = flowColumnLines(
      from,
      titleLines,
      x,
      () => {
        font('bold', 9.5);
        ink(PDF_COLOURS.ink);
      },
      4,
    );
    const blocks = readInsight(item.element, safe);
    if (!blocks.length) {
      blocks.push({ text: 'No insight text is available in Lens yet.' });
    }
    for (const block of blocks) {
      at.y += 2;
      const style = () => {
        font(block.heading ? 'bold' : 'normal', 8.5);
        ink(PDF_COLOURS.ink);
      };
      style();
      at = flowColumnLines(at, pdf.splitTextToSize(block.text, w) as Array<string>, x, style, 4);
    }
    return at;
  };

  /** Draws a table from `from` in the column [x, x + w]; returns where it ended. */
  const drawTable = (item: CardItem, from: PagePosition, x: number, w: number): PagePosition => {
    goTo(from.page);
    const model = readTable(item.element, safe);
    const scorecard = model ? [] : wandsIn(item.element).filter((wand) => wand.kind === 'table-cell');
    font('bold', 9.5);
    ink(PDF_COLOURS.ink);
    const titleLines = pdf.splitTextToSize(item.title, w) as Array<string>;
    pdf.text(titleLines, x, from.y + 3.5, { lineHeightFactor: 1.2 });
    let at = from.y + 3.5 + titleLines.length * 4;
    const shapedAttribute = item.element.querySelector(`[${LENS_PDF_SHAPED_ROWS_ATTRIBUTE}]`);
    const shapedRows = Number(
      shapedAttribute?.getAttribute(LENS_PDF_SHAPED_ROWS_ATTRIBUTE) ??
        item.element.getAttribute(LENS_PDF_TOTAL_ROWS_ATTRIBUTE) ??
        Number.NaN,
    );
    const note = model
      ? tableRowNote(
          {
            paintedNote: partialRowNote(item.element),
            partial: Boolean(partialRowNote(item.element)),
            shapedRows: Number.isFinite(shapedRows) ? shapedRows : undefined,
            shown: model.body.length,
          },
          canDraw,
        )
      : '';
    if (note) {
      font('italic', 7);
      ink(PDF_COLOURS.muted);
      const lines = pdf.splitTextToSize(note, w) as Array<string>;
      pdf.text(lines, x, at, { lineHeightFactor: 1.3 });
      at += lines.length * 3.1;
    }
    at += 1;
    let end: PagePosition;

    if (model && model.body.length) {
      autoTable(pdf, {
        body: model.body,
        bodyStyles: { fillColor: false, textColor: [...PDF_COLOURS.ink] },
        columnStyles: Object.fromEntries(model.align.map((align, index) => [index, { halign: align }])),
        didParseCell: (data: {
          section: string;
          column: { index: number };
          cell: { raw: unknown; styles: { font: unknown; halign: unknown; textColor: unknown } };
        }) => {
          // Measured and drawn in the fallback font when Helvetica cannot draw the cell.
          const family = needsFallback(data.cell.raw) ? fallbackFamily() : undefined;
          if (family) {
            data.cell.styles.font = family;
          }
          if (data.section === 'head') {
            data.cell.styles.halign = model.align[data.column.index] ?? 'left';
          }
          if (data.section === 'body' && typeof data.cell.raw === 'string' && isNegativeFigure(data.cell.raw)) {
            data.cell.styles.textColor = [...PDF_COLOURS.negative];
          }
        },
        head: model.head.length ? [model.head] : undefined,
        headStyles: { fillColor: [...PDF_COLOURS.head], fontStyle: 'bold', textColor: [...PDF_COLOURS.ink] },
        margin: {
          bottom: PDF_PAGE_HEIGHT_MM - CONTENT_BOTTOM_MM,
          left: x,
          right: PDF_PAGE_WIDTH_MM - x - w,
          top: CONTENT_TOP_MM,
        },
        rowPageBreak: 'avoid',
        showHead: 'everyPage',
        startY: at,
        styles: {
          cellPadding: { bottom: 1.5, left: 1.8, right: 1.8, top: 1.5 },
          font: 'helvetica',
          fontSize: 7.5,
          lineColor: [...PDF_COLOURS.rule],
          lineWidth: { bottom: 0.25, left: 0, right: 0, top: 0 },
          overflow: 'linebreak',
          valign: 'middle',
        },
        tableWidth: w,
        theme: 'plain',
        willDrawPage: () => decorate(pageNumber()),
      });
      const finalY = (pdf as unknown as { lastAutoTable: { finalY: number } }).lastAutoTable.finalY;
      end = { page: pageNumber(), y: finalY };
    } else if (scorecard.length) {
      // A one-row comparison table the board shows as a scorecard: typeset as small tiles.
      const gap = 2;
      const tileW = (w - gap * (scorecard.length - 1)) / scorecard.length;
      scorecard.forEach((cell, index) => {
        const tx = x + index * (tileW + gap);
        tileFrame(tx, at, tileW, 15);
        font('bold', 6);
        ink(PDF_COLOURS.label);
        pdf.text(
          capLines(pdf.splitTextToSize(safe(cell.label).toUpperCase(), tileW - 5) as Array<string>, 2),
          tx + 2.5,
          at + 4.5,
        );
        const value = safe(cell.value);
        let size = 11;
        font('bold', size);
        while (size > 6.5 && pdf.getTextWidth(value) > tileW - 5) {
          size -= 0.5;
          font('bold', size);
        }
        ink(isNegativeFigure(value) ? PDF_COLOURS.negative : PDF_COLOURS.ink);
        pdf.text(value, tx + 2.5, at + 12);
      });
      end = { page: from.page, y: at + 15 };
    } else {
      font('italic', 7.5);
      ink(PDF_COLOURS.muted);
      pdf.text('No rows shown in Lens for this period.', x, at + 2.5);
      end = { page: from.page, y: at + 4 };
    }
    const commentary = commentaryOf(item.element);
    if (commentary) {
      font('normal', 7);
      const lines = pdf.splitTextToSize(commentary, w) as Array<string>;
      end = flowColumnLines({ page: end.page, y: end.y + 1.5 }, lines, x, () => {
        font('normal', 7);
        ink(PDF_COLOURS.ink);
      });
    }
    return end;
  };

  // --- Laying out one canvas -----------------------------------------------------------------
  type Entry = {
    item: CardItem;
    x: number;
    w: number;
    relY: number;
    h: number;
    metric?: ReturnType<typeof metricLayout>;
    chart?: ReturnType<typeof chartLayout>;
  };
  type Band = { rows: Array<Array<Entry>>; fixedExtent: number; firstRowNeed: number };
  const sharesColumn = (a: { x: number; w: number }, b: { x: number; w: number }) =>
    a.x < b.x + b.w - 0.5 && b.x < a.x + a.w - 0.5;
  const flowsAcrossPages = (item: CardItem) => item.kind === 'table' || item.kind === 'insight';
  const rowNeed = (row: ReadonlyArray<Entry>) =>
    Math.max(
      ...row.map((entry) => (flowsAcrossPages(entry.item) ? TABLE_MIN_START_MM : Math.min(entry.h, contentHeight))),
    );

  const measureBands = (items: ReadonlyArray<CardItem>, canvasRect: DOMRect, k: number): Array<Band> =>
    groupIntoBands(items.map((item) => item.rect)).map((band) => {
      const members = band.map((index) => items[index]);
      const bandTop = Math.min(...members.map((item) => item.rect.top));
      // Native heights: a tile grows to fit its text; a chart keeps its board height or grows
      // to carry its commentary.
      const placed: Array<Entry> = members.map((item) => {
        const x = PDF_MARGIN_MM + (item.rect.left - canvasRect.left) * k;
        const w = item.rect.width * k;
        const relY = (item.rect.top - bandTop) * k;
        const boardH = item.rect.height * k;
        if (item.kind === 'metric') {
          const metric = metricLayout(item.element, item.title, w);
          return {
            h: Math.min(Math.max(Math.min(boardH, 40), metric.height), cardMaxHeight),
            item,
            metric,
            relY,
            w,
            x,
          };
        }
        if (item.kind === 'chart') {
          const chart = chartLayout(item, w, Math.min(boardH, cardMaxHeight));
          return { chart, h: chart.h, item, relY, w, x };
        }
        return { h: Math.min(boardH, cardMaxHeight), item, relY, w, x };
      });
      // Tiles in one row share a height, so the row reads as a set.
      for (const entry of placed) {
        if (entry.metric) {
          const row = placed.filter((other) => other.metric && Math.abs(other.relY - entry.relY) < 0.5);
          entry.h = Math.max(...row.map((other) => other.h));
        }
      }
      // Push a card down if a taller neighbour above it in the same column now overlaps it.
      // (A table's real length is only known once drawn; that is handled while drawing.)
      placed.sort((a, b) => a.relY - b.relY);
      for (let index = 0; index < placed.length; index += 1) {
        const current = placed[index];
        for (const above of placed.slice(0, index)) {
          if (sharesColumn(above, current) && !flowsAcrossPages(above.item)) {
            current.relY = Math.max(current.relY, above.relY + above.h + BAND_GAP_MM / 2);
          }
        }
      }
      placed.sort((a, b) => a.relY - b.relY || a.x - b.x);
      const rows: Array<Array<Entry>> = [];
      for (const entry of placed) {
        const row = rows[rows.length - 1];
        if (row && Math.abs(row[0].relY - entry.relY) < 0.5) {
          row.push(entry);
        } else {
          rows.push([entry]);
        }
      }
      const fixedExtent = Math.max(
        0,
        ...placed.map((entry) =>
          flowsAcrossPages(entry.item) ? entry.relY + TABLE_MIN_START_MM : entry.relY + entry.h,
        ),
      );
      return { firstRowNeed: rows.length ? rowNeed(rows[0]) : 0, fixedExtent, rows };
    });

  /**
   * Draws a band. A band that fits on a page is kept whole; a taller one flows row by row,
   * a row never splits, and a card always starts below whatever ended above it in its
   * column, including a table that ran on to later pages.
   */
  const drawBand = async (band: Band, keepWhole = true) => {
    ensureSpace(keepWhole && band.fixedExtent <= contentHeight ? band.fixedExtent : band.firstRowNeed);
    const origin: PagePosition = { ...cursor };
    let bandEnd: PagePosition = { ...cursor };
    const ends: Array<{ x: number; w: number; end: PagePosition }> = [];
    for (const row of band.rows) {
      checkStale();
      let at = advanceOnPages(origin, row[0].relY, CONTENT_TOP_MM, CONTENT_BOTTOM_MM);
      for (const entry of row) {
        for (const above of ends) {
          if (sharesColumn(above, entry)) {
            at = laterPosition(at, { page: above.end.page, y: above.end.y + BAND_GAP_MM / 2 });
          }
        }
      }
      if (at.y + rowNeed(row) > CONTENT_BOTTOM_MM && at.y > topOf(at.page) + 0.01) {
        at = { page: at.page + 1, y: CONTENT_TOP_MM };
      }
      for (const entry of row.filter((candidate) => !flowsAcrossPages(candidate.item))) {
        goTo(at.page);
        const h = Math.min(entry.h, CONTENT_BOTTOM_MM - at.y);
        let end = { page: at.page, y: at.y + h };
        if (entry.metric) {
          end = drawMetric(entry.metric, entry.x, at.y, entry.w, h);
        } else if (entry.chart) {
          await drawChart(entry.item, entry.chart, entry.x, at.y, entry.w, h);
        } else {
          await drawCapture(entry.item, entry.x, at.y, entry.w, h);
        }
        ends.push({ end, w: entry.w, x: entry.x });
        bandEnd = laterPosition(bandEnd, end);
        step(entry.item.element);
        checkStale();
      }
      for (const entry of row.filter((candidate) => flowsAcrossPages(candidate.item))) {
        const end =
          entry.item.kind === 'insight'
            ? drawInsight(entry.item, at, entry.x, entry.w)
            : drawTable(entry.item, at, entry.x, entry.w);
        ends.push({ end, w: entry.w, x: entry.x });
        bandEnd = laterPosition(bandEnd, end);
        step(entry.item.element);
        checkStale();
      }
    }
    goTo(bandEnd.page);
    cursor.page = bandEnd.page;
    cursor.y = bandEnd.y + BAND_GAP_MM;
  };

  // --- Body: sections, headings, readings and bands --------------------------------------------
  let k = 0;
  for (const section of sections) {
    let pendingHeading: { title: string; note: string } | undefined;
    let pendingReading: { text: string; note: string } | undefined;
    for (const child of Array.from(section.children) as Array<HTMLElement>) {
      checkStale();
      if (!child.querySelector(`[${LENS_PDF_CARD_ATTRIBUTE}]`)) {
        const heading = child.querySelector('h1, h2, h3, h4');
        const reading = child.hasAttribute(LENS_PDF_READING_ATTRIBUTE)
          ? child
          : child.querySelector<HTMLElement>(`[${LENS_PDF_READING_ATTRIBUTE}]`);
        if (heading) {
          const title = safe(heading.textContent ?? '');
          const note = safe((child.textContent ?? '').replace(heading.textContent ?? '', ''));
          pendingHeading = { note, title };
        }
        if (reading) {
          const text = safe(reading.textContent ?? '');
          if (text) {
            pendingReading = { note: safe(reading.getAttribute(LENS_PDF_READING_ATTRIBUTE) ?? ''), text };
          }
        }
        continue;
      }

      const cards = Array.from(child.querySelectorAll<HTMLElement>(`[${LENS_PDF_CARD_ATTRIBUTE}]`));
      const canvasRect = child.getBoundingClientRect();
      k = canvasRect.width > 0 ? CONTENT_WIDTH_MM / canvasRect.width : k;
      const items: Array<CardItem> = [];
      for (const element of cards) {
        const status = element.getAttribute(LENS_PDF_CARD_STATUS_ATTRIBUTE);
        const type = element.getAttribute(LENS_PDF_CARD_TYPE_ATTRIBUTE) ?? '';
        const title = titleOf(element);
        if (status === 'unconfigured' && type !== 'insight') {
          notIncluded.push(title || 'Untitled card');
          step(element);
          continue;
        }
        const kind: CardItem['kind'] =
          type === 'insight'
            ? 'insight'
            : type === 'metric'
              ? 'metric'
              : type === 'table'
                ? 'table'
                : chartHost(element)
                  ? 'chart'
                  : 'other';
        items.push({ element, kind, rect: element.getBoundingClientRect(), title });
      }
      const bands = measureBands(items, canvasRect, k);
      let firstBandWhole = true;

      // Heading and reading: kept with the start of what follows them.
      if (pendingHeading || pendingReading) {
        font('bold', 12.5);
        const headLines = pendingHeading
          ? (pdf.splitTextToSize(pendingHeading.title, CONTENT_WIDTH_MM) as Array<string>)
          : [];
        font('italic', 7.5);
        const noteLines = pendingHeading?.note
          ? (pdf.splitTextToSize(pendingHeading.note, CONTENT_WIDTH_MM) as Array<string>)
          : [];
        font('normal', 8.5);
        const readingLines = pendingReading
          ? (pdf.splitTextToSize(pendingReading.text, CONTENT_WIDTH_MM - 4) as Array<string>)
          : [];
        const headingHeight = headLines.length * 5.2 + noteLines.length * 3.3 + 3;
        const first = bands[0];
        // The first band is kept whole under the heading when both fit on a page; otherwise
        // it flows from under the heading rather than leaving the heading on its own.
        firstBandWhole = !first || first.fixedExtent <= contentHeight - headingHeight;
        const followNeed = readingLines.length
          ? Math.min(3, readingLines.length) * 4
          : first
            ? firstBandWhole
              ? first.fixedExtent
              : first.firstRowNeed
            : 0;
        if (cursor.y > topOf(cursor.page) + 0.01) {
          cursor.y += SECTION_GAP_MM - BAND_GAP_MM;
        }
        ensureSpace(Math.min(contentHeight, headingHeight + followNeed));
        goTo(cursor.page);
        if (headLines.length) {
          font('bold', 12.5);
          ink(PDF_COLOURS.ink);
          pdf.text(headLines, PDF_MARGIN_MM, cursor.y + 4.5, { lineHeightFactor: 1.15 });
          let at = cursor.y + 4.5 + headLines.length * 5.2;
          if (noteLines.length) {
            font('italic', 7.5);
            ink(PDF_COLOURS.muted);
            pdf.text(noteLines, PDF_MARGIN_MM, at - 1, { lineHeightFactor: 1.3 });
            at += noteLines.length * 3.3;
          }
          cursor.y = at + 0.5;
        }
        if (readingLines.length && pendingReading) {
          flowLines(
            readingLines,
            4,
            () => {
              font('normal', 8.5);
              ink(PDF_COLOURS.ink);
            },
            { bar: PDF_COLOURS.band, x: PDF_MARGIN_MM + 3 },
          );
          if (pendingReading.note) {
            font('italic', 7);
            flowLines(
              pdf.splitTextToSize(pendingReading.note, CONTENT_WIDTH_MM - 4) as Array<string>,
              3.4,
              () => {
                font('italic', 7);
                ink(PDF_COLOURS.muted);
              },
              { x: PDF_MARGIN_MM + 3 },
            );
          }
          cursor.y += 3;
        }
        pendingHeading = undefined;
        pendingReading = undefined;
      }

      for (const [index, band] of bands.entries()) {
        checkStale();
        await drawBand(band, index > 0 || firstBandWhole);
      }
    }
  }

  // --- Closing notes ------------------------------------------------------------------------------
  const closingNotes = [
    notIncluded.length ? `Not included because they are not set up in Lens yet: ${notIncluded.join('; ')}.` : '',
    missingCharts.length
      ? `Charts that could not be copied into this PDF (open the Lens to see them): ${missingCharts.join('; ')}.`
      : '',
    substitutedCharacters
      ? 'Some names use characters this PDF cannot print; they appear as "?". Lens shows them in full.'
      : '',
  ].filter(Boolean);
  for (const text of closingNotes) {
    font('italic', 7.5);
    cursor.y += 1.5;
    flowLines(pdf.splitTextToSize(text, CONTENT_WIDTH_MM) as Array<string>, 3.3, () => {
      font('italic', 7.5);
      ink(PDF_COLOURS.muted);
    });
  }
  checkStale();

  // Every card on the board must have been placed (or listed as not set up) exactly once.
  const unplaced = allCards.filter((card) => !handled.has(card));
  if (unplaced.length) {
    throw new LensPdfExportError(
      'missing-cards',
      `${unplaced.length} of ${allCards.length} cards could not be placed in the PDF: ${unplaced
        .map((card) => titleOf(card) || 'Untitled card')
        .join('; ')}.`,
    );
  }

  // --- Footer on every page ---------------------------------------------------------------------
  const pageCount = pdf.getNumberOfPages();
  for (let page = 1; page <= pageCount; page += 1) {
    decorate(page);
    pdf.setPage(page);
    font('normal', 7);
    ink(PDF_COLOURS.muted);
    pdf.text(
      truncateToWidth(pdf, `Luxury Escapes Lens  ·  ${header.title}`, CONTENT_WIDTH_MM * 0.75),
      PDF_MARGIN_MM,
      FOOTER_BASELINE_MM,
    );
    pdf.text(`Page ${page} of ${pageCount}`, PDF_PAGE_WIDTH_MM - PDF_MARGIN_MM, FOOTER_BASELINE_MM, {
      align: 'right',
    });
  }

  if (input.deliver) {
    input.deliver(pdf.output('arraybuffer'));
  } else {
    pdf.save(exportFileName(header.title, 'pdf'));
  }
  return { missingCharts, pageCount, substitutedCharacters };
}

/** Shortens text with an ellipsis so it fits `width` mm at the current font. */
function truncateToWidth(pdf: { getTextWidth: (text: string) => number }, text: string, width: number): string {
  if (pdf.getTextWidth(text) <= width) {
    return text;
  }
  let cut = text;
  while (cut.length > 1 && pdf.getTextWidth(`${cut}…`) > width) {
    cut = cut.slice(0, -1);
  }
  return `${cut.trimEnd()}…`;
}
